// libs/sdk/src/lib/wallet/adapters/wallet-connect-v2-adapter/multi-connect-provider.ts
import { ChainId } from "@one-inch-community/models";
import { isAddressEqual } from "viem";
import { EventEmitter } from "eventemitter3";
import { fromEvent, merge, tap } from "rxjs";
import { storage } from "@one-inch-community/core/storage";
import { genRandomHex } from "@one-inch-community/core/random";
var internalEvents = [
  "accountsChanged"
];
var MultiConnectProvider = class _MultiConnectProvider {
  constructor() {
    this.storage = /* @__PURE__ */ new Map();
    this.activeAddress = null;
    this.eventEmitter = new EventEmitter();
  }
  static async connect() {
    const provider = new _MultiConnectProvider();
    await provider.connect();
    return provider;
  }
  static async restoreConnect() {
    const provider = new _MultiConnectProvider();
    await provider.restoreConnect();
    return provider;
  }
  get signer() {
    if (this.activeAddress && this.storage.has(this.activeAddress)) {
      return this.storage.get(this.activeAddress)?.provider ?? null;
    }
    return null;
  }
  get chainId() {
    return this.signer?.chainId ?? ChainId.eth;
  }
  async connect() {
    const persistStorePrefix = genRandomHex(10);
    try {
      const provider = await makeProvider(persistStorePrefix);
      const subscription = this.listenEvents(provider);
      const { topic, uri } = await provider.signer.client.core.pairing.create();
      await provider.connect({
        pairingTopic: topic
      });
      const address = provider.accounts[0];
      if (this.storage.has(address)) {
        return;
      }
      const storage2 = {
        provider,
        topic,
        uri,
        address,
        persistStorePrefix,
        subscription
      };
      this.storage.set(address, storage2);
      this.activeAddress = address;
      this.updatePersist();
      this.eventEmitter.emit("accountsChanged", this.getAddresses());
    } catch (error) {
      await dropStorage(persistStorePrefix);
      throw error;
    }
  }
  async restoreConnect() {
    const persistData = this.getPersistData();
    for (const data of persistData) try {
      const provider = await makeProvider(data.persistStorePrefix);
      const subscription = this.listenEvents(provider);
      const address = provider.accounts[0];
      if (address !== data.address) {
        await dropStorage(data.persistStorePrefix);
        continue;
      }
      this.storage.set(address, {
        provider,
        uri: data.uri,
        topic: data.topic,
        address: data.address,
        persistStorePrefix: data.persistStorePrefix,
        subscription
      });
      if (data.isActive) {
        this.activeAddress = address;
      }
    } catch (error) {
      await dropStorage(data.persistStorePrefix);
      throw error;
    }
    this.eventEmitter.emit("accountsChanged", this.getAddresses());
  }
  setActiveAddress(address) {
    if (!address) return;
    this.activeAddress = address;
    this.eventEmitter.emit("accountsChanged", this.getAddresses());
  }
  async disconnect() {
  }
  isConnected() {
    return this.activeAddress !== null;
  }
  async request(args) {
    const method = args.method;
    if (method === "eth_requestAccounts" || method === "eth_accounts") {
      return this.getAddresses();
    }
    return await this.signer?.request(args) ?? null;
  }
  async enable() {
    return this.getAddresses();
  }
  on(event, listener) {
    if (internalEvents.includes(event)) {
      this.eventEmitter.on(event, listener);
      return;
    }
    this.signer?.on(event, listener);
  }
  removeListener(event, listener) {
    if (internalEvents.includes(event)) {
      this.eventEmitter.removeListener(event, listener);
      return;
    }
    this.signer?.removeListener(event, listener);
  }
  updatePersist() {
    const persistStorePrefixSet = /* @__PURE__ */ new Set();
    const data = [];
    this.storage.forEach(({ uri, topic, address, persistStorePrefix }) => {
      persistStorePrefixSet.add(persistStorePrefix);
      data.push({ uri, topic, address, persistStorePrefix, isActive: !!this.activeAddress && isAddressEqual(this.activeAddress, address) });
    });
    this.cleanOldStorage().catch();
    storage.set("wc2_persist", data);
  }
  getPersistData() {
    return storage.get("wc2_persist", JSON.parse) ?? [];
  }
  getAddresses() {
    const addresses = /* @__PURE__ */ new Set();
    this.activeAddress && addresses.add(this.activeAddress);
    this.storage.forEach((value) => {
      addresses.add(value.address);
    });
    return [...addresses];
  }
  listenEvents(provider) {
    let address;
    return merge(
      fromEvent(provider, "disconnect").pipe(
        tap(() => {
          this.storage.forEach((value) => {
            if (value.provider !== provider) return;
            address = value.address;
          });
          if (address && this.storage.has(address)) {
            const store = this.storage.get(address);
            this.storage.delete(address);
            store.subscription.unsubscribe();
            if (this.activeAddress === address) {
              const nextActiveAddressStore = [...this.storage.values()][0];
              this.activeAddress = nextActiveAddressStore?.address ?? null;
            }
            this.eventEmitter.emit("accountsChanged", this.getAddresses());
            this.updatePersist();
          }
        })
      )
    ).subscribe();
  }
  async cleanOldStorage() {
    const databasesList = await indexedDB.databases();
    const dbNameSet = /* @__PURE__ */ new Set();
    for (const store of this.storage.values()) {
      const dbName = await getDatabaseName(store.persistStorePrefix);
      dbNameSet.add(dbName);
    }
    for (const db of databasesList) {
      if (db.name && db.name.startsWith("wallet-connect") && !dbNameSet.has(db.name)) {
        await dropStorageByName(db.name);
      }
    }
  }
};
async function makeProvider(persistStorePrefix) {
  const options = await import("./wallet-connect-init-options-ACAHGVFG.js").then((m) => m.options());
  const { EthereumProvider } = await import("./ethereum-provider-YQG7GDUF.js");
  return await EthereumProvider.initProvider(options, persistStorePrefix);
}
async function dropStorageByName(name) {
  const { WalletConnectStorage } = await import("./ethereum-provider-YQG7GDUF.js");
  await WalletConnectStorage.dropStorageByName(name);
}
async function getDatabaseName(name) {
  const { WalletConnectStorage } = await import("./ethereum-provider-YQG7GDUF.js");
  return await WalletConnectStorage.getDatabaseName(name);
}
async function dropStorage(name) {
  const { WalletConnectStorage } = await import("./ethereum-provider-YQG7GDUF.js");
  return await WalletConnectStorage.dropStorage(name);
}
export {
  MultiConnectProvider
};
//# sourceMappingURL=multi-connect-provider-3SU46CE6.js.map
